import NearbyModel from './nearby-model';
import NearbyView from './nearby-view';

export default class NearbyPresenter {
  constructor(model, view, appInterface) {
    this.model = model || new NearbyModel();
    this.view = view || new NearbyView();
    this.appInterface = appInterface || this.createDefaultAppInterface();
  }

  createDefaultAppInterface() {
    return {
      showToast: (message, type, duration) => {
        if (window.app && window.app.showToast) {
          window.app.showToast(message, type, duration);
        }
      },
      navigateTo: (page) => {
        if (window.app && window.app.navigateTo) {
          window.app.navigateTo(page);
        }
      },
      showLightbox: (src) => {
        if (window.app && window.app.showLightbox) {
          window.app.showLightbox(src);
        }
      },
      showDialog: (dialogId) => {
        if (window.app && window.app.showDialog) {
          window.app.showDialog(dialogId);
        }
      },
      updateBookmarkBubble: () => {
        if (window.app && window.app.updateBookmarkBubble) {
          window.app.updateBookmarkBubble();
        }
      },
      reinitTooltips: () => {
        if (window.app && window.app.tooltip && window.app.tooltip.reinit) {
          window.app.tooltip.reinit();
        }
      },
      isAuthenticated: () => {
        return window.app?.isAuthenticated || false;
      },
      getUser: () => {
        return window.app?.user || {};
      },
      getBookmarks: () => {
        return window.app?.bookmarks || [];
      },
      setBookmarks: (bookmarks) => {
        if (window.app) {
          window.app.bookmarks = bookmarks;
        }
      },
      setShareUrl: (url) => {
        const shareUrlInput = document.querySelector('#share-url');
        if (shareUrlInput) {
          shareUrlInput.value = url;
        }
      }
    };
  }

  async init() {
    this.model.loadUserState();
    
    if (!this.model.isAuthenticated) {
      this.showGuestMode();
      return;
    }

    await this.initNearbyMap();
    await this.loadNearbyStories();
    this.setupEventListeners();
  }

  showGuestMode() {
    this.view.showGuestMapPlaceholder('#nearby-map');
    this.view.showAuthRequired('#nearby-posts-container');
  }

  async initNearbyMap() {
    try {
      const isDark = this.view.isDarkMode();
      const config = this.model.getMapConfig(isDark);

      this.view.initializeMap(config);
      this.view.addMapLayerControl((layerType) => {
        const styleUrl = this.model.getMapStyleConfig(isDark, layerType);
        this.view.setMapStyle(styleUrl);
      });
      
      try {
        const location = await this.model.getUserLocation();
        if (location) {
          this.model.setCurrentUserLocation(location);
          this.view.setMapCenter(location.lon, location.lat, 10);
          this.view.addUserLocationMarker(location);
          this.addRangeCircleToMap();
        }
      } catch (error) {
        console.warn('Could not get user location:', error);
      }
    } catch (error) {
      this.view.showMapError('#nearby-map', 'Gagal memuat peta');
    }
  }

  addRangeCircleToMap() {
    const circleData = this.model.getRangeCircleData(this.model.nearbyDistance);
    if (circleData) {
      this.view.addRangeCircle(circleData);
    }
  }

  async loadNearbyStories() {
    try {
      this.view.showLoading('#nearby-posts-container');
      
      const allStories = await this.model.getAllStoriesWithLocation();
      const nearbyStories = this.model.filterStoriesByDistance(allStories);
      
      this.model.setNearbyStories(nearbyStories);
      
      if (nearbyStories.length > 0) {
        const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(nearbyStories);
        this.view.renderStories(storiesWithBookmarkStatus, '#nearby-posts-container', this.model.currentUserLocation);
        
        setTimeout(() => {
          this.view.addStoryMarkers(nearbyStories);
        }, 500);
        
        await this.loadLocationNames(nearbyStories);
      } else {
        const distance = this.model.nearbyDistance;
        const emptyMessage = `Belum ada cerita dalam radius ${distance} km dari lokasi Anda. Silakan perbesar radius untuk melihat postingan dengan radius lebih besar.`;
        this.view.showEmptyState('#nearby-posts-container', emptyMessage);
      }
    } catch (error) {
      if (error.message.includes('401')) {
        this.view.showAuthRequired('#nearby-posts-container');
      } else {
        this.view.showEmptyState('#nearby-posts-container', 'Gagal memuat cerita terdekat');
      }
    }
  }

  addBookmarkStatusToStories(stories) {
    return stories.map(story => ({
      ...story,
      isBookmarked: this.model.isBookmarked(story.id)
    }));
  }

  async loadNearbyStories() {
    try {
      this.view.showLoading('#nearby-posts-container');
      
      // Gunakan method dedicated untuk nearby page
      const allStories = await this.model.getAllNearbyStories();
      const nearbyStories = this.model.filterStoriesByDistance(allStories);
      
      this.model.setNearbyStories(nearbyStories);
      
      if (nearbyStories.length > 0) {
        const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(nearbyStories);
        this.view.renderStories(storiesWithBookmarkStatus, '#nearby-posts-container', this.model.currentUserLocation);
        
        setTimeout(() => {
          this.view.addStoryMarkers(nearbyStories);
        }, 500);
        
        await this.loadLocationNames(nearbyStories);
      } else {
        const distance = this.model.nearbyDistance;
        const emptyMessage = `Belum ada cerita dalam radius ${distance} km dari lokasi Anda. Silakan perbesar radius untuk melihat postingan dengan radius lebih besar.`;
        this.view.showEmptyState('#nearby-posts-container', emptyMessage);
      }
    } catch (error) {
      if (error.message.includes('401')) {
        this.view.showAuthRequired('#nearby-posts-container');
      } else {
        this.view.showEmptyState('#nearby-posts-container', 'Gagal memuat cerita terdekat');
      }
    }
  }

  async loadLocationNames(stories) {
    for (const story of stories) {
      if (story.lat && story.lon) {
        try {
          const locationName = await this.model.getLocationName(story.lat, story.lon);
          this.view.updateLocationName(story.id, locationName);
        } catch (error) {
          this.view.updateLocationName(story.id, 'Lokasi tidak tersedia');
        }
      }
    }
  }

  setupEventListeners() {
    this.view.setupPostInteractionEventListeners(
      (storyId) => this.toggleBookmark(storyId),
      (storyId) => this.sharePost(storyId),
      (src) => this.showLightbox(src)
    );
  }

  toggleBookmark(storyId) {
    if (!this.model.isAuthenticated) {
      this.appInterface.showToast('Masuk terlebih dahulu untuk menyimpan postingan', 'warning');
      return;
    }
    
    const isCurrentlyBookmarked = this.model.isBookmarked(storyId);
    
    if (isCurrentlyBookmarked) {
      this.removeBookmark(storyId);
    } else {
      this.addBookmark(storyId);
    }
  }

  addBookmark(storyId) {
    const bookmark = this.model.addBookmark(storyId);
    
    if (bookmark) {
      this.updateGlobalBookmarks();
      this.view.updateBookmarkStatus(storyId, true);
      this.appInterface.showToast('Postingan berhasil disimpan', 'success');
    }
  }

  removeBookmark(storyId) {
    const success = this.model.removeBookmark(storyId);
    
    if (success) {
      this.updateGlobalBookmarks();
      this.view.updateBookmarkStatus(storyId, false);
      this.appInterface.showToast('Postingan dihapus dari bookmark', 'success');
    }
  }

  sharePost(storyId) {
    const url = `${window.location.origin}${window.location.pathname}#/post/${storyId}`;
    this.appInterface.setShareUrl(url);
    this.appInterface.showDialog('share-dialog');
  }

  showLightbox(src) {
    this.appInterface.showLightbox(src);
  }

  updateDistance(newDistance) {
    this.model.updateDistance(newDistance);
    
    this.loadNearbyStoriesOnly();
    this.updateRangeCircleOnly();
  }

  async loadNearbyStoriesOnly() {
    try {
      // Load stories tanpa init ulang map - gunakan method dedicated
      const allStories = await this.model.getAllNearbyStories();
      const nearbyStories = this.model.filterStoriesByDistance(allStories);
      this.model.setNearbyStories(nearbyStories);
      
      const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(nearbyStories);
      this.view.renderStories(storiesWithBookmarkStatus, '#nearby-posts-container', this.model.currentUserLocation);
      
      // Reload location names untuk stories baru
      await this.loadLocationNames(nearbyStories);
    } catch (error) {
      this.view.showEmptyState('#nearby-posts-container', 'Gagal memuat cerita terdekat');
    }
  }
  
  updateRangeCircleOnly() {
    const circleData = this.model.getRangeCircleData(this.model.nearbyDistance);
    if (circleData) {
      this.view.updateRangeCircle(circleData);
    }
  }

  updateGlobalBookmarks() {
    this.appInterface.setBookmarks([...this.model.bookmarks]);
    this.appInterface.updateBookmarkBubble();
  }

  async loadSidebarUsers() {
    if (!this.model.isAuthenticated) {
      this.view.clearSidebarUsers();
      return;
    }
  
    this.view.showSidebarLoading();
    
    try {
      const nearbyUsers = await this.model.loadNearbyUsersData(
        this.model.currentUserLocation, 
        this.model.nearbyDistance
      );
      
      this.view.renderSidebarUsers(nearbyUsers, this.model.currentUserLocation);
    } catch (error) {
      this.view.showSidebarError(this.model.nearbyDistance);
    }
  }

  cleanup() {
    this.view.cleanup();
  }

  refresh() {
    this.loadNearbyStories();
  }
}